// =============================================================================
// Pattern narrative renderer — wires PATTERN_CONTENT into the results page.
//
// Reads the Classification + LabInputs, picks the matching entry from
// PATTERN_CONTENT, interpolates `{{template_vars}}`, converts inline
// markdown (**bold** and `BADGE` codes), and renders into DOM slots on
// results.html.
//
// Covered sections:
//   - Overview paragraphs ("What your labs are telling us")
//   - Symptom integration (conditional: renders only if user reported symptoms)
//   - Abstract <dl> (Pattern / Confidence / Evidence strength / Severity /
//     Next steps) — synthesized from Classification + content banner
//   - Confidence paragraph (rich copy)
//   - Question Prompt List (per-pattern, replaces the static <ol>)
// =============================================================================

import type {
  Classification,
  LabInputs,
  LifeStage,
  SecondaryCandidate,
  SeverityTier,
} from '../engine/types.js';
import { PATTERN_CONTENT } from '../engine/pattern-content.js';
import type { PatternContent, QplEntry } from '../engine/pattern-content.js';
import { PATTERN_METADATA } from '../engine/pattern-metadata.js';
import { citationAnchorHtml, formatCitationLabel } from './citation-links.js';
import type { WinnerPatternId } from '../engine/arbitrate.js';
import {
  FERRITIN_THRESHOLDS,
  anemiaThresholdForLifeStage,
  mapLifeStageToSpecKey,
} from '../engine/index.js';

// -----------------------------------------------------------------------------
// Template variable resolution
// -----------------------------------------------------------------------------

function friendlyLifeStage(stage: LifeStage): string {
  switch (stage) {
    case 'non_pregnant_premenopausal':
      return 'non-pregnant premenopausal women';
    case 'pregnant_t1':
      return 'women in the first trimester of pregnancy';
    case 'pregnant_t2':
      return 'women in the second trimester of pregnancy';
    case 'pregnant_t3':
      return 'women in the third trimester of pregnancy';
    case 'postpartum_within_6mo':
      return 'women within 6 months postpartum';
    case 'perimenopausal':
      return 'perimenopausal women';
    case 'postmenopausal':
      return 'postmenopausal women';
    case 'none_of_these':
      return 'women in your life stage';
  }
}

/** Map the user's checked symptoms to a plain-English joined string. */
function listSymptoms(syms: LabInputs['symptoms'] | undefined): string {
  if (!syms || syms.length === 0) return '';
  const pretty: Record<NonNullable<LabInputs['symptoms']>[number], string> = {
    unusual_fatigue: 'unusual fatigue',
    hair_shedding: 'hair shedding',
    brittle_nails: 'brittle nails',
    restless_legs: 'restless legs',
    pica: 'pica',
    cold_intolerance: 'cold intolerance',
  };
  const names = syms.map((s) => pretty[s]);
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/** Stage-3 IDE upper threshold for the life stage (μg/L). */
function stage3Threshold(lifeStage: LifeStage): string {
  const key = mapLifeStageToSpecKey(lifeStage);
  const t = FERRITIN_THRESHOLDS[key] as { stage3IDEUpper?: number; stage3IDEUpperAtAge50?: number };
  const val = t.stage3IDEUpper ?? t.stage3IDEUpperAtAge50 ?? 25;
  return String(val);
}

/** Stage-0 replete minimum for the life stage (μg/L). */
function stage0Threshold(lifeStage: LifeStage): string {
  const key = mapLifeStageToSpecKey(lifeStage);
  const t = FERRITIN_THRESHOLDS[key] as { stage0ReleteMin?: number; adequateReserveMin?: number };
  const val = t.stage0ReleteMin ?? t.adequateReserveMin ?? 50;
  return String(val);
}

/** Stage-2 depletion lower threshold (μg/L) — only defined for non_pregnant_premenopausal; others default to 25. */
function stage2Threshold(lifeStage: LifeStage): string {
  if (lifeStage === 'non_pregnant_premenopausal') {
    return String(FERRITIN_THRESHOLDS.non_pregnant_premenopausal.stage2DepletionLower);
  }
  return stage3Threshold(lifeStage);
}

/** Build the template-variable map from Classification + LabInputs. */
export function buildTemplateVars(
  classification: Classification,
  inputs: LabInputs,
): Record<string, string> {
  const vars: Record<string, string> = {};

  vars.life_stage_friendly = friendlyLifeStage(inputs.lifeStage);
  vars.units = 'μg/L'; // stored values are internal-unit SI; renderer is SI-only for now

  if (inputs.ferritin !== undefined) {
    vars.ferritin = String(inputs.ferritin);
    vars.ferritin_value = `${inputs.ferritin} μg/L`;
  }
  if (inputs.hemoglobin !== undefined) {
    vars.hb = String(inputs.hemoglobin);
    vars.hb_value = `${inputs.hemoglobin} g/L`;
  }
  if (inputs.tsat !== undefined) {
    vars.tsat = String(inputs.tsat);
    vars.tsat_value = `${inputs.tsat} %`;
  }
  if (inputs.mcv !== undefined) {
    vars.mcv = String(inputs.mcv);
    vars.mcv_value = `${inputs.mcv} fL`;
  }
  if (inputs.rdw !== undefined) {
    vars.rdw = String(inputs.rdw);
  }
  if (inputs.crp !== undefined) {
    vars.crp = String(inputs.crp);
  }

  vars.stage_0_threshold = stage0Threshold(inputs.lifeStage);
  vars.stage_2_threshold = stage2Threshold(inputs.lifeStage);
  vars.stage_3_threshold = stage3Threshold(inputs.lifeStage);
  vars.anemia_threshold = String(anemiaThresholdForLifeStage(inputs.lifeStage));

  vars.listed_symptoms_plain_language = listSymptoms(inputs.symptoms);

  if (classification.mostInformativeNextTest) {
    vars.most_informative_next_test = classification.mostInformativeNextTest;
    // Mirror under a more specific name used by the insufficient_data
    // narrative — the engine's forward-look (next-test.ts) writes the
    // recommended marker into mostInformativeNextTest, so the two vars
    // resolve to the same value but read more naturally in copy:
    // "Adding {{recommended_next_test}} would..." vs the generic
    // {{most_informative_next_test}}.
    vars.recommended_next_test = classification.mostInformativeNextTest;
  }

  vars.additional_markers_for_fid = additionalMarkersForFid(inputs);

  vars.candidate_patterns_list = candidatePatternsList(classification);

  vars.what_would_sharpen_this = whatWouldSharpen(classification, vars);

  // Sentence-case confidence lead used by per-pattern confidence paragraphs
  // so the narrative voice matches the bar above ("Moderate confidence" →
  // "Moderately confident in...", "High" → "Confident in...", etc.). Patterns
  // template-interpolate `{{confidence_lead}}` rather than hard-coding
  // "Confident in" so the lead flexes per user input.
  vars.confidence_lead = confidenceLead(classification.confidenceTier);

  return vars;
}

/**
 * Sentence-case confidence lead matched to the engine's confidence tier.
 * Drives `{{confidence_lead}}` interpolation in pattern confidenceParagraph
 * strings so the narrative line agrees with the bar above it.
 */
function confidenceLead(tier: Classification['confidenceTier']): string {
  switch (tier) {
    case 'high':
      return 'Confident';
    case 'moderate':
      return 'Moderately confident';
    case 'low':
      return 'Less confident';
    case 'recalibration_flag':
      return 'Confidence flagged';
  }
}

/** Short labels used in the mixed-picture narrative to list the competing patterns. */
const PATTERN_SHORT_LABELS: Record<string, string> = {
  stage_0_iron_replete: 'Iron-replete (Stage 0)',
  stage_1_incipient_id: 'Incipient iron deficiency (Stage 1)',
  stage_2_iron_depletion_no_anemia: 'Iron depletion (Stage 2)',
  stage_3_iron_deficient_erythropoiesis: 'Iron-deficient erythropoiesis (Stage 3)',
  stage_4_iron_deficiency_anemia: 'Iron deficiency anemia (Stage 4)',
  inflammation_masks_iron_status: 'Inflammation-inflated ferritin',
  functional_iron_deficiency: 'Functional iron deficiency',
  anemia_of_chronic_disease_classic: 'Anemia of chronic disease',
  iron_overload_trigger_premenopausal: 'Iron overload trigger (premenopausal)',
  iron_overload_trigger_postmenopausal: 'Iron overload trigger (postmenopausal)',
  very_high_ferritin_any_stage: 'Very high ferritin (> 1000 μg/L)',
  microcytic_normal_iron_studies: 'Microcytic anemia with normal iron studies',
  macrocytic_anemia_signal: 'Macrocytic anemia signal',
};

/**
 * Comma-joined list of the competing patterns' short labels, for rendering in
 * the mixed-picture narrative. Empty string when no secondary candidates
 * (other patterns don't reference this variable).
 */
function candidatePatternsList(classification: Classification): string {
  if (!classification.secondaryCandidates || classification.secondaryCandidates.length === 0) {
    return '';
  }
  const labels = classification.secondaryCandidates.map(
    (c) => PATTERN_SHORT_LABELS[c.patternId] ?? c.patternId,
  );
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/**
 * Dynamic explainer for the "Are additional markers worth adding?" question in
 * the functional-iron-deficiency QPL. Filters out markers the user already
 * entered so the copy doesn't recommend things we already have — preserving
 * credibility that the tool is actually reading their inputs.
 */
function additionalMarkersForFid(inputs: LabInputs): string {
  const missing: string[] = [];
  if (inputs.stfr === undefined) missing.push('sTfR (soluble transferrin receptor)');
  if (inputs.retHe === undefined) missing.push('reticulocyte hemoglobin content');

  // Hepcidin is never a user input — it's a research tool — so it's always a
  // "worth knowing about" option regardless of what the user entered.
  const hepcidin = 'hepcidin (where available)';

  if (missing.length === 0) {
    return (
      "You've already entered the most useful secondary markers. " +
      capitalizeFirst(hepcidin) +
      ' is the main remaining option, though it is largely a research tool.'
    );
  }

  const items = [...missing, hepcidin];
  return capitalizeFirst(joinList(items)) + ' can sharpen the read.';
}

function joinList(items: string[]): string {
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function capitalizeFirst(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/**
 * Replace every `{{var_name}}` in `text` with the value from `vars`. If a
 * variable is missing (e.g., because the user did not enter that lab), the
 * placeholder is collapsed to "not provided" rather than left as raw
 * `{{tsat_value}}` text — which previously leaked into the rendered output
 * for mixed-picture candidate cards whose patterns referenced markers the
 * user had not supplied.
 */
export function interpolate(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_match, name: string) => {
    const value = vars[name.toLowerCase()];
    return value !== undefined ? value : 'not provided';
  });
}

// -----------------------------------------------------------------------------
// Inline markdown → HTML
// -----------------------------------------------------------------------------

/**
 * Convert the narrow Markdown subset used in pattern copy to HTML:
 *   **text**      → <strong>text</strong>
 *   `HIGH QUALITY` → <span class="badge high-quality">High quality</span>
 *   `CONSENSUS`    → <span class="badge consensus">Consensus</span>
 *   `PRELIMINARY`  → <span class="badge preliminary">Preliminary</span>
 *   `MODERATE`     → <span class="badge preliminary">Moderate</span>
 * Any other backtick literal is rendered as <code>…</code>.
 *
 * Input is pattern-authored copy (safe), not user input. Still escapes the
 * usual HTML characters as a precaution before re-inserting bold/badge markup.
 */
export function mdToHtml(text: string): string {
  let out = escapeHtml(text);

  // Backtick tokens — evidence badges or generic code literals.
  out = out.replace(/`([^`]+)`/g, (_match, inner: string) => {
    const badge = badgeForLiteral(inner.trim());
    if (badge) {
      return `<span class="badge ${badge.cls}">${badge.label}</span>`;
    }
    return `<code>${inner}</code>`;
  });

  // Bold markers.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  return out;
}

function badgeForLiteral(literal: string): { cls: string; label: string } | undefined {
  const upper = literal.toUpperCase();
  switch (upper) {
    case 'HIGH QUALITY':
    case 'HIGH_QUALITY':
      return { cls: 'high-quality', label: 'High quality' };
    case 'CONSENSUS':
      return { cls: 'consensus', label: 'Consensus' };
    case 'PRELIMINARY':
      return { cls: 'preliminary', label: 'Preliminary' };
    default:
      return undefined;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// -----------------------------------------------------------------------------
// Renderers
// -----------------------------------------------------------------------------

/**
 * For mixed_picture_multiple_patterns winners, compute a synthetic
 * "effective" Classification that carries the top candidate's pattern id +
 * metadata. The narrative renderers (abstract pattern, overview, symptom
 * integration, QPL, PDF transition) consume this effective classification
 * so the user reads the closest-match pattern as their main result.
 *
 * The original Classification is still used for:
 *   - The mixed-picture banner above the abstract
 *   - Confidence reasoning (mixed-picture-specific copy + 45% bar)
 *   - The "Other possible reads" section at the bottom (lower-ranked
 *     candidates)
 *   - Engine-level severity (max-of across candidates) — preserved on the
 *     effective so the severity row in the abstract reflects the worst
 *     case, not just the top candidate's
 *
 * For non-mixed classifications this is a passthrough.
 */
function buildEffectiveClassification(
  original: Classification,
): { effective: Classification; isMixedPicture: boolean } {
  if (original.patternId !== 'mixed_picture_multiple_patterns') {
    return { effective: original, isMixedPicture: false };
  }
  const candidates = original.secondaryCandidates ?? [];
  if (candidates.length === 0) {
    return { effective: original, isMixedPicture: true };
  }
  const top = candidates[0]!;
  const topMeta = PATTERN_METADATA[top.patternId as WinnerPatternId];
  if (!topMeta) {
    return { effective: original, isMixedPicture: true };
  }
  const effective: Classification = {
    ...original,
    patternId: top.patternId,
    laypersonName: topMeta.laypersonName,
    technicalName: topMeta.technicalName,
    strength: topMeta.strength,
    primaryCitations: [...topMeta.primaryCitations],
    supportingCitations: [...topMeta.supportingCitations],
    manifestRefs: [...topMeta.manifestRefs],
    // severity, confidenceTier, completeness, secondaryCandidates,
    // mostInformativeNextTest, caveats — preserved from original.
  };
  return { effective, isMixedPicture: true };
}

function titleCase(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function severityTierLine(classification: Classification): string {
  if (classification.patternId === 'stage_0_iron_replete') {
    return 'For your records. No iron-specific follow-up needed based on this pattern.';
  }
  switch (classification.severity) {
    case 'urgent':
      return 'See a clinician today. Same-day clinical contact is warranted; the banner at the top of this page has the details.';
    case 'prompt':
      return 'Bring to your next appointment. Worth a clinician conversation sooner than a routine visit, but not an emergency.';
    case 'routine':
      return 'For your records. Worth raising at your next clinical appointment if you want to.';
  }
}

/**
 * Turn a citation slug like `addo-2025` or `van-zeben-1990` into a
 * display-formatted author-year string: `Addo 2025`, `Van Zeben 1990`.
 * The slug convention is `lower-case-author(-parts)-YYYY` with the year
 * as the final hyphen-separated segment.
 */
/**
 * Plain-text citation label derived from an engine slug.
 * Retained as an exported helper for tests and legacy callers; the
 * results-page abstract now emits HTML anchors via citationAnchorHtml().
 */
export function formatCitation(slug: string): string {
  return formatCitationLabel(slug);
}

function confidenceTierLabel(tier: Classification['confidenceTier']): string {
  switch (tier) {
    case 'high':
      return 'High';
    case 'moderate':
      return 'Moderate';
    case 'low':
      return 'Low';
    case 'recalibration_flag':
      return 'Flagged for recalibration';
  }
}

/** Renders the overview paragraphs into `data-ff-bind="overviewParagraphs"`. */
function renderOverview(content: PatternContent, vars: Record<string, string>): void {
  const container = document.querySelector<HTMLElement>('[data-ff-bind="overviewParagraphs"]');
  if (!container) return;
  container.innerHTML = content.overview
    .map((p) => renderNarrativeBlock(p, vars))
    .join('');
}

/**
 * Render one narrative array entry. Most entries are plain paragraphs, but
 * an entry can opt into list or callout shapes for readability:
 *   - Lines beginning with "- "  → unordered list item.
 *   - Lines beginning with "> "  → highlighted callout block.
 *   - Anything else              → ordinary <p> with bold + badge support.
 *
 * Detection looks at every non-empty line in the entry; mixed shapes fall
 * back to plain <p>. Same content + same words; only the visual structure
 * changes.
 */
function renderNarrativeBlock(text: string, vars: Record<string, string>): string {
  const interpolated = interpolate(text, vars);
  const lines = interpolated.split('\n').map((l) => l.trim()).filter(Boolean);

  if (lines.length > 0 && lines.every((l) => l.startsWith('> '))) {
    const inner = lines.map((l) => l.slice(2).trim()).join(' ');
    return `<p class="narrative-callout">${mdToHtml(inner)}</p>`;
  }

  if (lines.length > 0 && lines.every((l) => l.startsWith('- '))) {
    const items = lines
      .map((l) => `<li>${mdToHtml(l.slice(2).trim())}</li>`)
      .join('');
    return `<ul class="narrative-list">${items}</ul>`;
  }

  return `<p>${mdToHtml(interpolated)}</p>`;
}

/**
 * Renders the symptom-integration section — the whole section toggles visibility
 * based on whether the user reported symptoms AND the pattern has content for it.
 */
function renderSymptomIntegration(
  content: PatternContent,
  inputs: LabInputs,
  vars: Record<string, string>,
): void {
  const section = document.querySelector<HTMLElement>('#symptoms');
  if (!section) return;

  const hasSymptoms = !!inputs.symptoms && inputs.symptoms.length > 0;
  const hasContent = !!content.symptomIntegration;

  if (!hasSymptoms || !hasContent) {
    section.hidden = true;
    return;
  }

  section.hidden = false;
  const body = section.querySelector<HTMLElement>('[data-ff-bind="symptomIntegration"]');
  if (body) {
    const paragraphs = content.symptomIntegration!;
    body.innerHTML = paragraphs
      .map((p) => renderNarrativeBlock(p, vars))
      .join('');
  }
}

/** Synthesizes the abstract <dl> from Classification + pattern content. */
function renderAbstractDl(
  classification: Classification,
  content: PatternContent,
  vars: Record<string, string>,
): void {
  const patternDd = document.querySelector<HTMLElement>('[data-ff-bind="abstractPattern"]');
  const confidenceDd = document.querySelector<HTMLElement>('[data-ff-bind="abstractConfidence"]');
  const evidenceDd = document.querySelector<HTMLElement>('[data-ff-bind="abstractEvidence"]');
  const severityDd = document.querySelector<HTMLElement>('[data-ff-bind="abstractSeverity"]');
  const nextStepsDd = document.querySelector<HTMLElement>('[data-ff-bind="abstractNextSteps"]');

  if (patternDd) {
    patternDd.innerHTML = `<strong>${escapeHtml(classification.technicalName)}.</strong> ${mdToHtml(interpolate(content.banner.body, vars))}`;
  }

  if (confidenceDd) {
    const tier = confidenceTierLabel(classification.confidenceTier);
    confidenceDd.innerHTML = `<strong>${tier}.</strong> ${buildConfidenceShortCopy(classification)}`;
  }

  if (evidenceDd) {
    // Meta-patterns don't make an evidence-based clinical claim, so the
    // "Evidence strength" row is misleading on those reads. Hide both
    // the dt label and the dd value when the winner is a meta-pattern.
    const isMetaPattern =
      classification.patternId === 'insufficient_data' ||
      classification.patternId === 'no_established_pattern' ||
      classification.patternId === 'mixed_picture_multiple_patterns';
    if (isMetaPattern) {
      evidenceDd.hidden = true;
      const prevDt = evidenceDd.previousElementSibling;
      if (prevDt && prevDt.tagName === 'DT') {
        (prevDt as HTMLElement).hidden = true;
      }
    } else {
      evidenceDd.hidden = false;
      const prevDt = evidenceDd.previousElementSibling;
      if (prevDt && prevDt.tagName === 'DT') {
        (prevDt as HTMLElement).hidden = false;
      }
      const badge = badgeForStrength(classification.strength);
      // Each citation renders as a DOI-linked anchor so a reader can jump
      // straight from the abstract to the source paper. `citationAnchorHtml`
      // returns trusted HTML (escaped internally); we inject via innerHTML.
      const citations = classification.primaryCitations.length
        ? classification.primaryCitations.map(citationAnchorHtml).join(', ')
        : 'see methodology';
      evidenceDd.innerHTML = `${badge ? `<span class="badge ${badge.cls}">${badge.label}</span> ` : ''}primary evidence. Citations: ${citations}.`;
    }
  }

  if (severityDd) {
    severityDd.textContent = severityTierLine(classification);
  }

  if (nextStepsDd) {
    // Short one-sentence synthesis. The full QPL intro (`whatThisMeansIntro`)
    // lives in the next-steps section below and ends with a colon that
    // introduces the QPL — rendering it here would leave an orphan colon.
    nextStepsDd.innerHTML = mdToHtml(interpolate(content.abstractNextSteps, vars));
  }
}

/**
 * Visual percent for the confidence bar. Base percent driven by completeness;
 * reduced for recalibration flag (inflammation or supplementation caveat on
 * a ferritin-based pattern); overridden for the meta-patterns.
 */
/**
 * Visual percent for the confidence bar. Bands are designed to keep the
 * bar's color, the tier label above it ("Moderate confidence"), and the
 * narrative lead below it ("Moderately confident in...") all consistent
 * for any given input set.
 *
 * Engine tier (from arbitrate.computeConfidenceTier) → percent:
 *   high (full panel)        → 90
 *   high (cbc + ferritin)    → 80
 *   moderate (cbc only)      → 65   (was 55; that read borderline)
 *   low (insufficient_data)  → 25
 *   low (mixed_picture)      → 45
 *   recalibration_flag       → base − 25, floored at 40
 */
function computeConfidencePercent(c: Classification): number {
  if (c.patternId === 'insufficient_data') return 25;
  if (c.patternId === 'mixed_picture_multiple_patterns') return 45;

  const base =
    c.completeness === 'tier_3_full_panel'
      ? 90
      : c.completeness === 'tier_2_cbc_plus_ferritin'
        ? 80
        : 65; // tier_1_cbc_only

  if (c.confidenceTier === 'recalibration_flag') {
    return Math.max(base - 25, 40);
  }
  return base;
}

/**
 * Bar fill class. Boundaries align with the percent bands above so the
 * color always agrees with the tier label and narrative lead:
 *   ≥ 75 → high (90, 80)
 *   ≥ 50 → moderate (65)
 *   <  50 → low (45, 25)
 */
function percentFillClass(percent: number): string {
  if (percent >= 75) return 'confidence-high';
  if (percent >= 50) return 'confidence-moderate';
  return 'confidence-low';
}

/**
 * One-sentence explanation of WHY the confidence sits where it does for
 * this specific result. Renders under the percent label so the bar number
 * isn't naked context. Conditionalized on tier + completeness + pattern id;
 * never returns generic filler.
 */
function confidenceReason(c: Classification): string {
  if (c.patternId === 'insufficient_data') {
    return 'No lab values were entered, so no pattern can be classified.';
  }
  if (c.patternId === 'mixed_picture_multiple_patterns') {
    return 'Two or more patterns fit your values about equally well, so a single read would oversimplify.';
  }
  if (c.confidenceTier === 'recalibration_flag') {
    return 'Inflammation or recent iron supplementation may be inflating ferritin, so this pattern is read with caution.';
  }
  switch (c.completeness) {
    case 'tier_3_full_panel':
      return 'You provided a full iron panel and CBC; this is the strongest input set the engine reads against.';
    case 'tier_2_cbc_plus_ferritin':
      return 'You provided ferritin and a CBC, which is enough to fit this pattern with high confidence.';
    case 'tier_1_cbc_only':
      return 'You provided a CBC without ferritin or TSAT, so the read rests on red-cell indices alone.';
  }
}

/**
 * One-sentence "what would raise confidence" hint, rendered next to the bar.
 * Names the specific test (or specific action, for the recalibration_flag
 * case) so the user has a concrete way to improve the read on a future draw.
 *
 * Returns the empty string only when the user is already at full confidence
 * (tier_3_full_panel + high tier).
 */
function whatWouldSharpen(
  c: Classification,
  _vars: Record<string, string>,
): string {
  // No improvement available — already at full panel + high tier.
  if (
    c.completeness === 'tier_3_full_panel' &&
    c.confidenceTier === 'high'
  ) {
    return '';
  }

  // Recalibration flag: confidence is reduced because ferritin is being
  // distorted by inflammation or recent iron supplementation. The fix is
  // not "add another marker" — it's wait for the confound to clear and
  // retest.
  if (c.confidenceTier === 'recalibration_flag') {
    return 'Retest 2 to 4 weeks after the inflammation or supplementation clears to get a confidence reading without the recalibration flag.';
  }

  // No labs entered at all.
  if (c.patternId === 'insufficient_data') {
    return 'Add at least ferritin to get a research-based read; ferritin plus a CBC and TSAT moves this to high confidence.';
  }

  // Two patterns competing — the engine's mostInformativeNextTest names
  // the disambiguator for this specific classification.
  if (c.patternId === 'mixed_picture_multiple_patterns') {
    if (c.mostInformativeNextTest) {
      return `Adding ${c.mostInformativeNextTest} would help separate the competing patterns and raise confidence.`;
    }
    return 'A fuller iron panel would help separate the competing patterns and raise confidence.';
  }

  // Substantive patterns: name the specific tests still missing relative
  // to the user's current completeness tier.
  if (c.completeness === 'tier_1_cbc_only') {
    return 'Adding ferritin and TSAT would let the engine confirm this pattern and move to high confidence.';
  }
  if (c.completeness === 'tier_2_cbc_plus_ferritin') {
    return 'Adding TSAT would confirm whether iron delivery to cells is the limiting factor and move this to high confidence.';
  }
  return '';
}

function buildConfidenceShortCopy(c: Classification): string {
  switch (c.confidenceTier) {
    case 'high':
      return 'Supporting markers consistent with this pattern rather than pointing elsewhere.';
    case 'moderate': {
      // Inline the specific next test from the engine rather than the
      // generic "most informative next test" phrasing.
      const next = c.mostInformativeNextTest;
      if (next) {
        return `This pattern fits your values. Adding ${next} would confirm it, or show that a different pattern fits better.`;
      }
      return 'This pattern fits your values. A fuller iron panel would confirm it, or show that a different pattern fits better.';
    }
    case 'low':
      return 'The data does not separate cleanly or is missing a key marker; a clinician conversation is warranted.';
    case 'recalibration_flag':
      return 'Ferritin interpretation is flagged by inflammation or recent supplementation; read with care.';
  }
}

function badgeForStrength(
  strength: Classification['strength'],
): { cls: string; label: string } | undefined {
  switch (strength) {
    case 'HIGH_QUALITY':
      return { cls: 'high-quality', label: 'High quality' };
    case 'CONSENSUS':
      return { cls: 'consensus', label: 'Consensus' };
    case 'PRELIMINARY':
      return { cls: 'preliminary', label: 'Preliminary' };
  }
}

/**
 * Renders the standalone confidence paragraph below the per-lab section,
 * with a visual fill bar + tier label + percent + "what would sharpen this"
 * hint stacked above the pattern-specific confidence copy. The abstract at
 * the top of the page shows the tier label only; this is where the meter
 * carries weight.
 */
function renderConfidenceParagraph(
  classification: Classification,
  content: PatternContent,
  vars: Record<string, string>,
): void {
  const slot = document.querySelector<HTMLElement>('[data-ff-bind="confidenceParagraphRich"]');
  if (!slot) return;
  const tier = confidenceTierLabel(classification.confidenceTier);
  const percent = computeConfidencePercent(classification);
  const fillClass = percentFillClass(percent);
  const reason = confidenceReason(classification);
  const sharpen = whatWouldSharpen(classification, vars);

  const meter =
    `<div class="confidence-meter">` +
    `<div class="confidence-bar" role="img" aria-label="Confidence ${percent}%">` +
    `<div class="confidence-bar-fill ${fillClass}" style="width: ${percent}%"></div>` +
    `</div>` +
    `<p class="confidence-meter-label"><strong>${tier} confidence (${percent}%).</strong></p>` +
    `<p class="confidence-reason">${escapeHtml(reason)}</p>` +
    (sharpen ? `<p class="confidence-sharpen">${escapeHtml(sharpen)}</p>` : '') +
    `</div>`;

  slot.innerHTML = meter + `<p>${mdToHtml(interpolate(content.confidenceParagraph, vars))}</p>`;
}

/** Replaces the static QPL `<ol>` with pattern-specific questions. */
function renderQpl(
  content: PatternContent,
  inputs: LabInputs,
  vars: Record<string, string>,
): void {
  const slot = document.querySelector<HTMLElement>('[data-ff-bind="qpl"]');
  if (!slot) return;
  const intro = document.querySelector<HTMLElement>('[data-ff-bind="whatThisMeansIntro"]');
  if (intro) {
    intro.innerHTML = mdToHtml(interpolate(content.whatThisMeansIntro, vars));
  }
  const visible = content.questionPromptList.filter((q) => !shouldSkipQpl(q, inputs));
  slot.innerHTML = visible.map((q: QplEntry) => renderQplEntry(q, vars)).join('');
}

/**
 * Returns true when the user already entered every marker in the entry's
 * skipIf.hasAllOf list — i.e., the question recommends adding a marker the
 * user already has, which undermines credibility.
 */
function shouldSkipQpl(q: QplEntry, inputs: LabInputs): boolean {
  if (!q.skipIf?.hasAllOf) return false;
  return q.skipIf.hasAllOf.every((field) => {
    const value = (inputs as unknown as Record<string, unknown>)[field];
    return value !== undefined && value !== null && value !== '';
  });
}

function renderQplEntry(q: QplEntry, vars: Record<string, string>): string {
  return `
    <li>
      <p class="qpl-q"><strong>${mdToHtml(interpolate(q.question, vars))}</strong></p>
      <p class="qpl-a">${mdToHtml(interpolate(q.explainer, vars))}</p>
    </li>
  `;
}

/** Replaces the PDF transition paragraph. */
function renderPdfTransition(content: PatternContent, vars: Record<string, string>): void {
  const slot = document.querySelector<HTMLElement>('[data-ff-bind="pdfTransition"]');
  if (!slot) return;
  slot.innerHTML = mdToHtml(interpolate(content.pdfTransition, vars));
}

// -----------------------------------------------------------------------------
// Mixed-picture banner + "Other possible reads" section
// -----------------------------------------------------------------------------

/**
 * Banner above the abstract that surfaces the multi-pattern fact when the
 * engine returns mixed_picture_multiple_patterns. The abstract below carries
 * the top candidate's read; the banner sets the framing so the user knows
 * they're looking at the closest of several fitting patterns.
 */
function renderMixedPictureBanner(
  original: Classification,
  effective: Classification,
  isMixedPicture: boolean,
): void {
  // Toggle every banner-marked element with data-ff-show="ifMixedPicture" /
  // data-ff-hide="ifMixedPicture".
  document
    .querySelectorAll<HTMLElement>('[data-ff-show="ifMixedPicture"]')
    .forEach((el) => { el.hidden = !isMixedPicture; });
  document
    .querySelectorAll<HTMLElement>('[data-ff-hide="ifMixedPicture"]')
    .forEach((el) => { el.hidden = isMixedPicture; });

  if (!isMixedPicture) return;

  const slot = document.querySelector<HTMLElement>('[data-ff-bind="mixedPictureBanner"]');
  if (!slot) return;
  const topName = escapeHtml(effective.laypersonName);
  slot.innerHTML =
    `<strong>Two or more research patterns fit your values about equally well.</strong> ` +
    `The closest match is shown below — your <em>${topName}</em> read. ` +
    `The other patterns that also fit are listed near the bottom of this page, ` +
    `each with what would confirm or rule it out.`;
}

/**
 * Render the "Other possible reads" section at the bottom of the page.
 * Contains the lower-weighted candidates (everything except the top one,
 * which is already rendered as the main narrative). Each candidate card
 * carries a fit summary, the disambiguating test, the would-be overview,
 * and a per-candidate severity chip.
 *
 * `secondaryCandidates` is sorted highest-weight-first by arbitrate.ts;
 * we slice from index 1 to skip the top candidate.
 */
function renderMixedPictureCandidates(
  classification: Classification,
  vars: Record<string, string>,
): void {
  const isMixed = classification.patternId === 'mixed_picture_multiple_patterns';
  if (!isMixed) return;

  // Lead sentence — names the disambiguating test and frames the cards below.
  const leadSlot = document.querySelector<HTMLElement>('[data-ff-bind="mixedPictureLead"]');
  if (leadSlot) {
    const next = classification.mostInformativeNextTest;
    const lead = next
      ? `Each pattern below also fits your values. Adding <strong>${escapeHtml(next)}</strong> would help separate them most cleanly. Click any pattern to see what would confirm or rule it out.`
      : 'Each pattern below also fits your values. Click any pattern to see what would confirm or rule it out.';
    leadSlot.innerHTML = lead;
  }

  // Render the candidate cards — skip index 0 (top candidate is the main
  // narrative).
  const slot = document.querySelector<HTMLElement>('[data-ff-bind="mixedPictureCandidates"]');
  if (!slot) return;
  const candidates = (classification.secondaryCandidates ?? []).slice(1);
  if (candidates.length === 0) {
    slot.innerHTML = '';
    return;
  }
  slot.innerHTML = candidates
    .map((c) => renderCandidateCard(c, vars))
    .join('');
}

/** Single accordion card for one alternative-read candidate. */
function renderCandidateCard(
  candidate: SecondaryCandidate,
  vars: Record<string, string>,
): string {
  const meta = PATTERN_METADATA[candidate.patternId as WinnerPatternId];
  const content = PATTERN_CONTENT[candidate.patternId as WinnerPatternId];
  if (!meta || !content) return '';

  const sevAction = severityActionShort(candidate.severity);
  const overviewHtml = content.overview
    .map((p) => `<p>${mdToHtml(interpolate(p, vars))}</p>`)
    .join('');

  return `
    <details class="candidate-card">
      <summary>
        <span class="candidate-title">${escapeHtml(meta.laypersonName)}</span>
        <span class="candidate-severity-chip" data-severity="${candidate.severity}">${escapeHtml(sevAction)}</span>
      </summary>
      <div class="candidate-body">
        <p class="candidate-fit"><strong>Why it fits.</strong> ${escapeHtml(candidate.fitSummary)}</p>
        <p class="candidate-disambig"><strong>What would confirm or rule it out.</strong> ${escapeHtml(candidate.disambiguatingTest)}</p>
        <hr class="candidate-divider" />
        <p class="candidate-section-label">If this is the read</p>
        <div class="candidate-overview">${overviewHtml}</div>
        <p class="candidate-action"><strong>Next step.</strong> ${escapeHtml(content.abstractNextSteps)}</p>
      </div>
    </details>
  `;
}

/** Severity tier → action-focused short label, mirrors severityLabel() on results.ts. */
function severityActionShort(severity: SeverityTier): string {
  switch (severity) {
    case 'urgent': return 'See a clinician today';
    case 'prompt': return 'Bring to your next appointment';
    case 'routine': return 'For your records';
  }
}

// -----------------------------------------------------------------------------
// Main entry
// -----------------------------------------------------------------------------

export function renderPatternNarrative(
  classification: Classification,
  inputs: LabInputs,
): void {
  // For mixed_picture winners, switch the user-facing narrative onto the
  // top candidate's content + metadata. The original classification is
  // still used downstream for confidence reasoning, the banner above the
  // abstract, and the "Other possible reads" section at the bottom.
  const { effective, isMixedPicture } = buildEffectiveClassification(classification);
  const patternId = effective.patternId as WinnerPatternId;
  const content = PATTERN_CONTENT[patternId];
  if (!content) return;

  const vars = buildTemplateVars(effective, inputs);

  renderMixedPictureBanner(classification, effective, isMixedPicture);
  renderAbstractDl(effective, content, vars);
  renderOverview(content, vars);
  renderSymptomIntegration(content, inputs, vars);
  // Confidence reasoning needs the original classification so the
  // mixed-picture-specific copy + 45% bar still fire when relevant; the
  // narrative paragraph body is taken from the effective pattern's content.
  renderConfidenceParagraph(classification, content, vars);
  renderQpl(content, inputs, vars);
  renderPdfTransition(content, vars);
  renderMixedPictureCandidates(classification, vars);

  // For insufficient_data the confidence + per-lab sections have nothing
  // substantive to show (pattern fires precisely because we couldn't classify
  // from the inputs, and the lab-data table is either empty or very thin).
  // Hide both rather than display empty / low-signal boxes.
  if (classification.patternId === 'insufficient_data') {
    hideSection('lab-data');
    hideSection('confidence');
  } else {
    showSection('lab-data');
    showSection('confidence');
  }
}

function hideSection(id: string): void {
  const el = document.getElementById(id);
  if (el) (el as HTMLElement).hidden = true;
}

function showSection(id: string): void {
  const el = document.getElementById(id);
  if (el) (el as HTMLElement).hidden = false;
}
